import { ErrorResponse } from "api";
import React, { useCallback, useContext } from "react";
import { useLocalStorage } from "shared/hooks";
import { WithChildren } from "types";
import {
  GetPermissionsParams,
  GetPermissionsResponse,
  LoginPayload,
  LoginResponse,
  getPermissionsRequest,
  loginRequest,
} from "../api";
import { useCookies } from "react-cookie";
import { COOKIE_NAME } from "data/constants";

export const AUTH_STORAGE_KEY = "AUTH";

export type UserData = Partial<LoginResponse>;

export enum LoginErrorCode {
  InvalidCredentials = "InvalidCredentials",
  UserDisabled = "UserDisabled",
}

type LoginErrorResponse = ErrorResponse<{
  message: string;
  errorCode: LoginErrorCode;
}>;

interface LoginOptions {
  onSuccess?: (res: UserData) => void;
  onError?: (e: LoginErrorResponse) => void;
}

interface GetPermissionsOptions {
  onSuccess?: (res: GetPermissionsResponse) => void;
  onError?: (e: any) => void;
}

interface AutheticationContextData extends UserData {
  logIn: (userData: LoginPayload, options?: LoginOptions) => void;
  logOut: () => void;
  getPermissions: (
    payload: GetPermissionsParams,
    options?: GetPermissionsOptions
  ) => void;
}

const AutheticationContext = React.createContext(
  {} as AutheticationContextData
);

export function useAuth() {
  return useContext(AutheticationContext);
}

export const AuthenticationProvider = ({ children }: WithChildren) => {
  const [userData, setUserData, clearUserData] = useLocalStorage<UserData>(
    AUTH_STORAGE_KEY,
    {}
  );
  const [cookies, setCookie, removeCookie] = useCookies([COOKIE_NAME]);
  const { user, availableAccounts, defaultAccount, permissions } =
    userData || {};
  const domain = process.env.REACT_APP_COOKIE_DOMAIN;
  const duration = Number(process.env.REACT_APP_COOKIE_DURATION);
  const isHttps = Number(process.env.REACT_APP_IS_HTTPS) === 1
  const secure = isHttps;


  const getPermissions = useCallback(
    async (payload: GetPermissionsParams, options?: GetPermissionsOptions) => {
      try {
        const newPermissions = await getPermissionsRequest(payload);

        setUserData({
          ...userData,
          permissions: newPermissions,
        });

        options?.onSuccess?.(newPermissions);
      } catch (error: any) {
        // TODO handle error
        options?.onError?.(error);
        return error;
      }
    },
    [userData]
  );

  const logIn = useCallback(
    async (payload: LoginPayload, options?: LoginOptions) => {
      try {
        const res = await loginRequest(payload);
        const {token, ...responseWithoutToken} = res;
        setUserData(responseWithoutToken);
        setCookie(COOKIE_NAME, res.token, { path: '/', maxAge: 60 * duration, domain, secure})
        options?.onSuccess?.(res);
      } catch (error: any) {
        // TODO send alert to dev team if there is a network error
        options?.onError?.(error as LoginErrorResponse);
        return error;
      }
    },
    []
  );

  const logOut = useCallback(() => {
    clearUserData();
    removeCookie(COOKIE_NAME, { path: '/', domain});
  }, []);

  return (
    <AutheticationContext.Provider
      value={{
        user,
        availableAccounts,
        defaultAccount,
        permissions,
        logIn,
        logOut,
        getPermissions,
      }}
    >
      {children}
    </AutheticationContext.Provider>
  );
};
